import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { userManagementService } from '../../services/userManagement';
import { useTranslation } from 'react-i18next';

// Extracted Components
import UserStatsCards from './user-management/components/UserStatsCards';
import UserFilters from './user-management/components/UserFilters';
import UsersTable from './user-management/components/UsersTable';
import SuspensionLogsTable from './user-management/components/SuspensionLogsTable';
import UserActionsMenu from './user-management/components/UserActionsMenu';
import UserDialogs from './user-management/components/UserDialogs';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-tabpanel-${index}`}
      aria-labelledby={`user-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const UserManagement: React.FC = () => {
  const { user: currentUser } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [users, setUsers] = useState<any[]>([]);
  const [suspensionLogs, setSuspensionLogs] = useState<any[]>([]);
  const [userStats, setUserStats] = useState<any>(null);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  
  // Dialog states
  const [suspendDialog, setSuspendDialog] = useState(false);
  const [bulkActionDialog, setBulkActionDialog] = useState(false);
  const [createSellerDialogOpen, setCreateSellerDialogOpen] = useState(false);
  
  // Form data
  const [suspendForm, setSuspendForm] = useState({
    reason: '',
    duration: 'permanent',
    customDays: 30,
  });
  
  const [filters, setFilters] = useState<{
    role: 'buyer' | 'admin' | 'seller' | '';
    status: 'active' | 'suspended' | 'deactivated' | '';
    search: string;
  }>({
    role: '',
    status: '',
    search: '',
  });

  const [createSellerForm, setCreateSellerForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    company_name: '',
  });
  const [createSellerError, setCreateSellerError] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: usersData, error: usersError } = await userManagementService.getAllUsers({
        role: filters.role || undefined,
        status: filters.status || undefined,
        search: filters.search || undefined,
      });
      if (!usersError) {
        setUsers(usersData || []);
      }

      const { data: logsData } = await userManagementService.getSuspensionLogs();
      setSuspensionLogs(logsData || []);

      const stats = await userManagementService.getUserStatistics();
      setUserStats(stats);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleSuspendUser = async () => {
    if (!selectedUser || !currentUser) return;
    
    setProcessing(true);
    try {
      let suspendedUntil: Date | undefined;
      if (suspendForm.duration !== 'permanent') {
        suspendedUntil = new Date();
        suspendedUntil.setDate(suspendedUntil.getDate() + suspendForm.customDays);
      }

      const { error } = await userManagementService.suspendUser(
        selectedUser.id,
        currentUser.id,
        suspendForm.reason,
        suspendedUntil
      );

      if (!error) {
        setSuspendDialog(false);
        fetchData();
      }
    } catch (error) {
      console.error('Error suspending user:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleUnsuspendUser = async (userId: string) => {
    if (!currentUser) return;
    setProcessing(true);
    try {
      const { error } = await userManagementService.unsuspendUser(userId, currentUser.id);
      if (!error) fetchData();
    } catch (error) {
      console.error('Error unsuspending user:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleDeactivateUser = async (userId: string) => {
    if (!currentUser) return;
    if (window.confirm(t('admin.deactivateUser') + '?')) {
      setProcessing(true);
      try {
        const { error } = await userManagementService.deactivateUser(userId, currentUser.id, t('admin.adminAction'));
        if (!error) fetchData();
      } catch (error) {
        console.error('Error deactivating user:', error);
      } finally {
        setProcessing(false);
      }
    }
  };

  const handleBulkSuspend = async () => {
    if (!currentUser || selectedUsers.length === 0) return;
    setProcessing(true);
    try {
      const result = await userManagementService.bulkSuspendUsers(
        selectedUsers,
        currentUser.id,
        suspendForm.reason,
        suspendForm.duration !== 'permanent' ? new Date(Date.now() + suspendForm.customDays * 24 * 60 * 60 * 1000) : undefined
      );
      alert(`${t('warranty.pending')}: ${result.success}, ${t('warranty.failed')}: ${result.failed}`);
      setBulkActionDialog(false);
      setSelectedUsers([]);
      fetchData();
    } catch (error) {
      console.error('Error in bulk suspend:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleExportUserData = async (userId: string) => {
    const { data, error } = await userManagementService.exportUserData(userId);
    if (!error && data) {
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `user-data-${userId}.json`;
      a.click();
    }
  };

  const handleCreateSeller = async () => {
    setCreateSellerError('');
    if (!createSellerForm.email) {
      setCreateSellerError(t('auth.emailRequired'));
      return;
    }
    if (!createSellerForm.password) {
      setCreateSellerError(t('auth.passwordRequired'));
      return;
    }
    if (createSellerForm.password.length < 6) {
      setCreateSellerError(t('auth.passwordMinLength'));
      return;
    }
    if (createSellerForm.password !== createSellerForm.confirmPassword) {
      setCreateSellerError(t('auth.passwordsMustMatch', { defaultValue: 'Passwords must match' }));
      return;
    }

    setProcessing(true);
    try {
      const { error } = await userManagementService.createSellerAccount({
        email: createSellerForm.email,
        password: createSellerForm.password,
        full_name: createSellerForm.full_name || undefined,
        company_name: createSellerForm.company_name || undefined,
      });

      if (error) {
        setCreateSellerError((error as any)?.message || t('common.error'));
        return;
      }

      setCreateSellerDialogOpen(false);
      setCreateSellerForm({ email: '', password: '', confirmPassword: '', full_name: '', company_name: '' });
      await fetchData();
    } catch (error: any) {
      setCreateSellerError(error?.message || t('common.error'));
    } finally {
      setProcessing(false);
    }
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUsers(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  };

  if (loading && !users.length) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' } }}>
          {t('admin.userManagement')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
          {t('admin.manageUsersPermissionsActivity')}
        </Typography>
      </Box>

      <UserStatsCards userStats={userStats} />

      <UserFilters 
        filters={filters} 
        setFilters={setFilters} 
        selectedCount={selectedUsers.length}
        onCreateSeller={() => {
          setCreateSellerError('');
          setCreateSellerDialogOpen(true);
        }}
        onBulkSuspend={() => setBulkActionDialog(true)}
      />

      <Paper sx={{ width: '100%', bgcolor: 'background.paper', borderRadius: '8px', border: '1px solid', borderColor: 'divider' }}>
        <Tabs
          value={tabValue}
          onChange={(e, v) => setTabValue(v)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label={t('admin.allUsers')} />
          <Tab label={t('admin.suspensionHistory')} />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <UsersTable 
            users={users} 
            selectedUsers={selectedUsers}
            onToggleSelection={toggleUserSelection}
            onSelectAll={(checked) => setSelectedUsers(checked ? users.map(u => u.id) : [])}
            onMenuOpen={handleMenuOpen}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <SuspensionLogsTable logs={suspensionLogs} />
        </TabPanel>
      </Paper>

      <UserActionsMenu 
        anchorEl={anchorEl}
        selectedUser={selectedUser}
        onClose={() => setAnchorEl(null)}
        onViewActivity={() => {}} // Placeholder as activity dialog logic was minimal
        onSuspendClick={() => setSuspendDialog(true)}
        onUnsuspendClick={handleUnsuspendUser}
        onExportData={handleExportUserData}
        onDeactivateClick={handleDeactivateUser}
      />

      <UserDialogs 
        suspendDialogOpen={suspendDialog}
        onSuspendClose={() => setSuspendDialog(false)}
        selectedUser={selectedUser}
        suspendForm={suspendForm}
        setSuspendForm={setSuspendForm}
        onSuspendUser={handleSuspendUser}
        bulkSuspendOpen={bulkActionDialog}
        onBulkSuspendClose={() => setBulkActionDialog(false)}
        selectedUsersCount={selectedUsers.length}
        onBulkSuspend={handleBulkSuspend}
        createSellerOpen={createSellerDialogOpen}
        onCreateSellerClose={() => setCreateSellerDialogOpen(false)}
        createSellerForm={createSellerForm}
        setCreateSellerForm={setCreateSellerForm}
        createSellerError={createSellerError}
        setCreateSellerError={setCreateSellerError}
        onCreateSeller={handleCreateSeller}
        processing={processing}
      />
    </Container>
  );
};

export default UserManagement;